import userAuthStore from '../../../../common/stores/user-auth/user-auth.store';
import { Button, Divider, Dropdown, MenuProps, Modal, Space, Image, Switch } from 'antd';
import React, { ReactNode, useEffect, useState } from 'react';
import { ReactComponent as LogOut } from 'assets/icons/eiq-connect/logout.svg';
import { ReactComponent as Edit } from 'assets/icons/eiq-connect/edit.svg';
import { ReactComponent as PhoneIcon } from 'assets/icons/eiq-connect/phone.svg';
import { ReactComponent as LogOutWhite } from 'assets/icons/eiq-connect/logout-white.svg';
import { ReactComponent as Email } from 'assets/icons/email.svg';
import './eiq-connect-account.scss';
import InfoField from 'common/components/info-block/info-field/info-field';
import { useNavigate } from 'react-router';
import { observer } from 'mobx-react-lite';
import eiqConnectStore from 'features/eiq-connect/store/eiq-connect.store';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import ConfirmationModal from 'features/eiq-connect/components/confirmation-modal/confirmation-modal';
import { formatPhone } from '../../../../common/helpers/phone.helper';
import dayjs from 'dayjs';
import Feature from 'common/components/feature/feature';
import { FeatureName } from 'common/enums/feature-name.enum';
import { AccountFastAccessBlock } from './components/account-qr-code';
import { EllipsisOutlined } from '@ant-design/icons';
import residentsService from 'common/services/residents-service/residents.service';
import { getPhoneComparer } from 'features/eiq-connect/helpers/phone.helper';
import { useLocation } from 'react-router-dom';
import TopHeader from '../../components/top-header/top-header';
import configurationStore from 'common/stores/configuration/configuration.store';
import { ProtectText } from 'common/components/protect-text/protect-text';
import { ModalProvider } from 'common/components/modal/modal-context/modal-context';
import { MassNotificationBlock } from './components/mass-notification-block';
import { notificaitonService } from 'common/services/notification-service/notification.service';
import { NotificationConfirmationModal } from 'features/eiq-connect/modals/notification-confirmation-modal/notification-confirmation-modal';
import { NotificationType } from 'common/enums/notification-type.enum';
import NotificationsPreviewButton from '../notifications/notifications-preview-button/notifications-preview-button';
import ViewPhoto from 'common/components/view-photo/view-photo';
import featureStore from 'common/stores/features/feature.store';
import secureService from 'features/eiq-connect/service/security-service/security.service';
import { notification } from 'common/utils/notification';
import { IS_BIOMETRIC_AUTH_ENABLED } from 'common/stores/user-auth/user-auth.constants';

const VerticalEllipsis = () => <EllipsisOutlined style={{ color: '#505762', transform: 'rotate(90deg)' }} />;

export const AccountInfo = observer(({ className }: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMainPath = location.pathname.endsWith('/account');
  const [accountInfo, setAccountInfo] = useState(eiqConnectStore.residentDetails);
  const isShowPin = configurationStore.configuration?.default.isShowPinConnect;
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);

  useEffect(() => {
    if (eiqConnectStore.residentDetails?.id) {
      setAccountInfo(eiqConnectStore?.residentDetails);
    }
  }, [eiqConnectStore?.residentDetails]);

  const GetVacationField = (startDateString: string | undefined, endDateString: string | undefined): ReactNode => {
    const isVacation = startDateString && endDateString && dayjs().isBetween(startDateString, endDateString, 'day', '[]');

    if (!isVacation) {
      return <InfoField value={'Home'} label="Vacation" valueClassName="eiq-label primary" />;
    } else {
      return <InfoField value={'Vacationing'} label="Vacation" valueClassName="eiq-label warning" />;
    }
  };

  const preferredNotificationType = eiqConnectStore.preferredNotificationType;

  const logOutModal = useModal({});
  const deleteAccountModal = useModal({});

  const logOut = () => {
    userAuthStore.logOut();
    userAuthStore.manualLogout = true;
  };

  const handleClickExtraMenu = (e: any) => {
    if (e.key === 'Privacy') {
      window.open('https://www.entranceiq.net/privacy-policy.html', '_blank');
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'Privacy',
      label: 'Privacy Policy',
      // icon: <EllipsisOutlined />,
      onClick: handleClickExtraMenu,
    },
  ];

  const onDeleteAccountClick = () => {
    residentsService.requestDeletion(userAuthStore.webGuestResidentId!).then((result) => {
      logOut();
    });
  };

  const modalsInModalProvider = [
    {
      checkModalCondition: notificaitonService.checkIfNotificationModalShouldBeShown,
      modalComponent: NotificationConfirmationModal,
    },
  ];

  const [biometricAuth, setBiometricAuth] = useState<any>(null);
  const [isBiometricAuthAvailable, setBiometricAuthAvailable] = useState(false);

  useEffect(() => {
    const set = async () => {
      try {
        const value = localStorage.getItem(IS_BIOMETRIC_AUTH_ENABLED);
        if (value) {
          const result = JSON.parse(value);

          if (result.login === eiqConnectStore.residentDetails!.email) {
            setBiometricAuth(result);
          }
        }

        const isBiometricAuthAvailable = await secureService.isBiometricAuthAvailable();
        const result = isBiometricAuthAvailable && featureStore.isConnectFIDEnabled;

        setBiometricAuthAvailable(result);
      } catch (error: any) {
        notification.error({ message: error });
      }
    };

    set();
  }, []);

  const toggleBiometricAuth = async (value: boolean) => {
    if (value) {
      const creds = await secureService.generateAndStoreKeyPair();

      if (!creds) {
        notification.error({ message: 'Some error occurred', duration: 5 });
        return;
      }

      await residentsService.registerCredential(userAuthStore.webGuestResidentId!, creds);

      const value = {
        login: eiqConnectStore.residentDetails!.email,
        isEnabled: true,
      };

      localStorage.setItem(IS_BIOMETRIC_AUTH_ENABLED, JSON.stringify(value));
      setBiometricAuth(value);

      notification.success({ message: 'Login with FaceID enabled', duration: 5 });
    } else {
      localStorage.setItem(IS_BIOMETRIC_AUTH_ENABLED, JSON.stringify({ isEnabled: false }));
      secureService.remove();
      setBiometricAuth(null);
      notification.success({ message: 'Login with FaceID disabled', duration: 5 });
    }
  };

  return (
    <div className={`eiq-guests-page ${className ?? ''}`}>
      <div className={`eiq-header ${isMainPath && 'main-page'}`}>
        <h1 className="eiq-title">Account</h1>
        <div className="eiq-top-header">
          <TopHeader />
          <Space size={10} className="eiq-action">
            <Button size="large" className="round-primary" icon={<LogOut />} onClick={logOutModal.showModal} />
            <Button
              size="large"
              className="round-primary"
              icon={<Edit />}
              onClick={() => {
                navigate('/eiq-connect/account/edit');
              }}
            />
            <Dropdown rootClassName="android-photo-menu" menu={{ items }} arrow={false}>
              <Button size="large" className="round-primary" icon={<VerticalEllipsis />} />
            </Dropdown>
            <NotificationsPreviewButton />
          </Space>
        </div>
      </div>
      <div className="eiq-content">
        <div className=" eiq-account">
          <div className="eiq-account-user-info-container">
            <div>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                <div>
                  <p className="eiq-user-name">{`${accountInfo?.firstName} ${accountInfo?.lastName}`}</p>
                  <div className="eiq-community">
                    <InfoField
                      label="Community"
                      value={accountInfo?.addresses?.find((a) => a.address.id === selectedAddressId)?.address?.street?.community?.name}
                    />
                  </div>
                </div>
                {featureStore.isEIQProfileEnabled && accountInfo?.photoUrl && <ViewPhoto photoUrl={accountInfo.photoUrl} />}
              </div>
            </div>
            <Feature name={FeatureName.FastAccess}>
              <AccountFastAccessBlock selectedAddressId={selectedAddressId} setSelectedAddressId={setSelectedAddressId} />
            </Feature>
          </div>

          <div className="eiq-account-user-info-container">
            <div className="eiq-account-info-container">
              <div className="eiq-account-phones">
                <span>Phone numbers</span>
                {accountInfo?.phones
                  .slice()
                  .sort(getPhoneComparer())
                  .map((item, index) => (
                    <div className="contact-phone" key={`contact-phone-${index}`}>
                      <PhoneIcon />
                      <span>{formatPhone(item.countryCode, item.number)}</span>
                      <span className={`eiq-label ${item.label?.toLowerCase()}`}>{item.label}</span>
                    </div>
                  ))}
              </div>
              <div className="eiq-account-info-block">
                <Divider className="account-divider" />
                <InfoField label="Email" value={accountInfo?.email} valueIcon={<Email />} />
                <Divider className="account-divider" />
                {isShowPin && (
                  <>
                    <InfoField label="PIN" value={<ProtectText text={accountInfo?.pin} />} />
                    <Divider className="account-divider" />
                  </>
                )}
                <div className="eiq-account-vacation">{GetVacationField(accountInfo?.vacationStartDate, accountInfo?.vacationEndDate)}</div>
                <Divider className="account-divider" />

                <div className="eiq-account-notification">
                  <InfoField label="Notification" value={''} />
                  {preferredNotificationType === NotificationType.SMS ? (
                    <InfoField label="" value={'Receive SMS notifications'} />
                  ) : preferredNotificationType === NotificationType.Email ? (
                    <InfoField label="" value={'Receive email notifications'} />
                  ) : preferredNotificationType === NotificationType.Push ? (
                    <InfoField label="" value={'Receive push notifications'} />
                  ) : (
                    <InfoField label="" value={'Do not receive any notifications'} />
                  )}
                </div>

                <Divider className="account-divider" />

                <ModalProvider modals={modalsInModalProvider}>
                  <MassNotificationBlock />
                </ModalProvider>

                {isBiometricAuthAvailable && (
                  <>
                    <Divider className="account-divider" />
                    <div>
                      <InfoField label="Enable FaceID for logging in" value={''} bodyClassName="biometric-auth-toggle" />
                      <Switch checked={!!biometricAuth?.isEnabled} onChange={toggleBiometricAuth} />
                    </div>
                  </>
                )}

                <Divider className="account-divider" />

                <span className="eiq-link" onClick={deleteAccountModal.showModal}>
                  Delete my account
                </span>

                <Divider className="account-divider" />
                <InfoField label="Application version" value={process.env.REACT_APP_RELEASE_NAME || ''} />
                <Divider className="account-divider" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationModal
        title="Log out"
        isOpen={logOutModal?.isOpenModal}
        onClose={logOutModal?.hideModal}
        mainText={'Are you sure you want to log out?'}
        handleConfirm={logOut}
        confirmBtnText={'Log out'}
        confirmBtnIcon={<LogOutWhite />}
      />
      <ConfirmationModal
        title="Delete account"
        isOpen={deleteAccountModal?.isOpenModal}
        onClose={deleteAccountModal?.hideModal}
        mainText="Deleting your account is not reversible. To restore your account, you will need to contact the community administrator. Are you sure you want to delete it?"
        handleConfirm={onDeleteAccountClick}
        confirmBtnText="Ok"
        confirmBtnIcon={null}
      />
    </div>
  );
});
